/**
 * @description PropertyModel Domian类处理
 */

import { Attribute, AttributeValue } from "@modules/product/model/domain/attribute/AttributeValue";
import { ValueType } from "@modules/product/model/domain/attribute/ValueTypeEnum";
import { AttributeOption } from "@modules/product/model/domain/data/AttributeOption";
import { AttrOption } from "@modules/product/model/domain/propertyModel/AttrOption";
import { Profile } from "@modules/product/model/domain/propertyModel/Profile";
import { IAttributeValue } from "@modules/product/product/model/domain/IAttributeValue";
import { ProfileModel } from "@modules/property-model/model/domain/ProfileModel";
import { PropertyModel } from "@modules/property-model/model/domain/PropertyModel";
import { SkuAttributeModel } from "@modules/property-model/model/domain/SkuAttributeModel";
import { deserialize } from "typescript-json-serializer";

export class PropertyModelManager {

    /*************************** Chore ************************ */
    /**
     * @description 
     * 判断当前属性能否反选
     * 属性类型:
     * 连续:输入类型不支持选择，返回false
     * 单离散:
     *  属性必填:不可反选
     *  属性非必填:可反选
     * 多离散
     *  属性必填:当前选中项数量大于一个时,可反选,当前选中项数量等于一个时,不可反选
     *  属性非必填:可反选
     * 
     */
    public static canInvert(attribute: SkuAttributeModel): boolean {
        // 默认不可反选
        let result = false;
        if (!attribute.selectType) {
            throw new Error("Missing 'selectType' attribute");
        }
        if (attribute.selectType == "NON") {
            return result
        }

        // 单离散
        if (attribute.selectType == "SINGLE" && !attribute.required) {
            result = true;
        }
        // 多离散
        if (attribute.selectType == "MULTI") {
            if (!attribute.required) {
                result = true;
            }
            const selectedIds = attribute.value as Array<number>;
            // 获取当前选中项数量
            const selectedCount = selectedIds.length;
            // 当前选中项数量大于一个时,可反选
            if (selectedCount > 1) {
                result = true;
            }
        }
        return result
    }

    /**
     * @description 获取当前激活的Profile
     * @param pm 
     * @returns 
     */
    public static getActive(pm: PropertyModel): ProfileModel | undefined {
        return pm.profileModels.find(item => {
            return item.isActive
        })
    }

    /**
     * @description 检查是否存在不合法的连续型属性
     */
    public static hasIllegalContinuousAttribute(profileModel: ProfileModel): boolean {
        const illegalAttr = profileModel.attributeList.find(item => {
            return item.selectType == "NON" && item.isLegal == false;
        })
        return illegalAttr ? true : false;
    }

    /**
     * 保留连续型属性的错误信息,但是重置连续
     * @param propertyModel 
     */
    public static resetContinuousAttribute(oldPM: PropertyModel, newPM: PropertyModel): PropertyModel {
        //TODO(后期优化处理) const _oldPM = deserialize(oldPM,PropertyModel);
        const _oldPM = angular.fromJson(angular.toJson(oldPM));
        newPM.profileModels.forEach((profileItem, profileIndex) => {
            profileItem.attributeList.forEach((item, index) => {
                if (!item.isLegal) {
                    _oldPM.profileModels[profileIndex].attributeList[index].errorMsg = item.errorMsg;
                } else {
                    _oldPM.profileModels[profileIndex].attributeList[index].errorMsg = null;
                }
            })
        })
        return _oldPM
    }

    /**
     * @description 检验PropertyModel是否验证通过
     * 判断依据:
     *  1.PropertyModel中的isLegal
     *  2.attributeList中的每个属性的isLegal
     * @returns true:验证通过 | false:验证不通过
     */
    public static checkPropertyModelLegal(pm: PropertyModel): boolean {
        // 默认验证通过
        let isLegal = true;
        if (!angular.isUndefined(pm.isLegal) && !pm.isLegal) {
            isLegal = false;
        }
        if (!pm.profileModels) {
            // throw new Error("");
            isLegal = false;
        }
        // 遍历Profile中的AttributeList,若存在isLegal为false的字段,则验证不通过
        const profileLength = pm.profileModels.length;
        for (let i = 0; i < profileLength; i++) {
            // 找出isLegal为false的属性
            const skuAttribute = pm.profileModels[i].attributeList;
            const hasLegal = skuAttribute.find(item => {
                return !item.isLegal
            })
            if (!angular.isUndefined(hasLegal)) {
                isLegal = false;
            }
        }
        return isLegal
    }

    /**
     * @description 获取属性选中项
     * @param attributeOptons 
     * @returns 
     */
    public static getActiveOption(attributeOptons: Array<AttrOption>): Array<AttrOption> | undefined {
        if (attributeOptons.length == 0) {
            return [];
        }
        return attributeOptons.filter(item => {
            return item.isSelected
        })
    }

    /**
     * @description 获取属性中选中的选项id
     * @param attributeOptons 
     * @returns 
     */
    public static getActiveOptionsId(attributeOptons: Array<AttrOption>): Array<number> {
        let result: Array<number> = [];
        attributeOptons.forEach(item => {
            if (item.isSelected) {
                result.push(Number(item.id));
            }
        })
        return result;
    }

    /**
     * @description 更新连续型属性值
     * @param value 
     * @param attribute 
     */
    public static updateContinuousAttribute(value: string, attribute: SkuAttributeModel): SkuAttributeModel {
        // 检查当前属性是否是连续型属性
        if (attribute.selectType != "NON") {
            throw new Error("");
        }
        // 属性值类型转换
        attribute.value = attribute.valueType == "Number" ? Number(value) : value;
        // 将属性isDirty设为true
        attribute.isDirty = true;

        return attribute;
    }

    /**
     * @description 单离散值属性选项更改后调用,用于做属性设为dirty等选项更改后处理
     * @param targetId 
     * @param attribute 
     * @param isCancel 是否取消选中 
     */
    private static updateMultiDiscreteDirty(targetId: number, attribute: SkuAttributeModel, isCancel: boolean = false) {
        const attributeOptions = attribute.attrOptions as Array<AttrOption>;
        // 获取目标项Option
        const targetOption = attributeOptions.find(item => {
            return item.id == targetId;
        })
        if (!targetOption) {
            throw new Error("");
        }
        attribute.isDirty = true;
        const attributeValue = attribute.value as Array<number> || [];
        // 设置属性选中状态
        targetOption.isSelected = isCancel ? false : true;

        attributeValue.length = 0;
        attributeOptions.forEach(item => {
            if (item.isSelected) {
                attributeValue.push(item.id);
            }
        })
        attribute.value = attributeValue;
    }
    /**
     * @description 单离散值属性选项更改后调用,用于做属性设为dirty等选项更改后处理
     * @param targetId 
     * @param attribute 
     */
    private static updateSingleDiscreteDirty(targetId: number, attribute: SkuAttributeModel) {
        const attributeOptions = attribute.attrOptions as Array<AttrOption>;
        // 获取目标项Option
        const targetOption = attributeOptions.find(item => {
            return item.id == targetId;
        })
        if (!targetOption) {
            throw new Error("");
        }
        targetOption.isSelected = true;
        attribute.isDirty = true;

        const attributeValue = attribute.value as Array<number> || [];
        attributeValue.length = 0;
        attributeValue.push(targetId);
        attribute.value = attributeValue;
    }

    /**
     * 
     * @description 更新单离散类型属性值
     * 点击单离散属性选项后触发
     *  
     *  没有选中项:
     *      将目标选项选中
     *     
     *  已有选中项:
     *      目标项是否已被选中
     *          目标选项已被选中(点击了已经选中的选项):判断能否反选     
     *                  能:取消选中
     *                  不能:不做处理,直接返回
     *          未选中
     *              将当前选中项取消选中,目标选项设为选中
     * 
     * @param targetId 
     * @param attribute 
     * @returns 
     */
    public static selectSingleDiscreteOption(targetId: number, attribute: SkuAttributeModel): SkuAttributeModel {
        // 预防传入id数字类型为String,因此多做一次类型转换
        const tId = Number(targetId);
        // 检查属性是否是单离散类型
        if (attribute.selectType != "SINGLE") {
            throw new Error("Attribute is not single attribute");
        }
        if (angular.isUndefined(attribute.attrOptions) || attribute.attrOptions?.length == 0) {
            throw new Error("Attribute don have attrOptions");
        }

        const attributeOptions = attribute.attrOptions as Array<AttrOption>;
        // 获取当前选中项id列表
        let activeOptions = this.getActiveOptionsId(attributeOptions);
        // 获取目标项Option
        const targetOption = attributeOptions.find(item => {
            return item.id == targetId;
        })
        if (!targetOption) {
            throw new Error("");
        }

        // 没有选中项时,将目标项设为选中
        if (activeOptions.length == 0) {
            this.updateSingleDiscreteDirty(targetId, attribute);
            return attribute;
        }

        // 检查当前选项是否已被选中,若已被选中则需要检查能否反选
        const hasSelected = activeOptions.indexOf(tId);

        if (hasSelected > -1) {
            // 目标选项已被选中,判断能否反选
            if (this.canInvert(attribute)) {
                // 能反选:取消选中
                this.updateSingleDiscreteDirty(targetId, attribute);
                return attribute;
            } else {
                // 不能反选:重新将当前目标选中
                targetOption.isSelected = true;
                return attribute;
            }
        } else {
            // 目标项未选中:将其他选项取消选中,
            attributeOptions.forEach((item, index, sourceArr) => {
                sourceArr[index].isSelected = false;
            })
            // 目标选项设为选中
            this.updateSingleDiscreteDirty(targetId, attribute);
            return attribute;
        }
    }

    /**
     * @description 判断离散型属性能否选择(注意：这里不是考虑反选的情况,仅仅做enable和readonly的判断处理)
     */
    public static canSelect(targetId: number, attribute: SkuAttributeModel): boolean {
        // 默认可以选中
        let canSelect = true;
        // 目标选项
        const targetOption = attribute.attrOptions?.find(item => {
            return item.id == targetId;
        })

        // 属性本身readonly或不可用时,属性选项不可被选中
        if (attribute.readOnly || !attribute.enable) {
            canSelect = false;
        }
        // 目标选项自身不可用时,不可被选中
        if (targetOption && canSelect && !targetOption.enable) {
            canSelect = false;
        }

        return canSelect;
    }


    /********************************************************* */


    /**
     * @desc 更新多离散值属性
     * 点击选项后逻辑处理:
     *  目前项已被选中:
     *      能否反选: 能:取消选中
     *               不能:不作任何处理
     *  目前项未被选中:
     *      选中目标项
     * @param targetId 
     * @param attribute 
     */
    public static selectMultiDiscreteOption(targetId: number, attribute: SkuAttributeModel): SkuAttributeModel {
        // 检查属性是否是单离散类型
        if (attribute.selectType != "MULTI") {
            throw new Error("Attribute is not MULTI attribute");
        }

        if (!attribute.attrOptions) {
            throw new Error("Attribute don have attrOptions");
        }

        const attributeOptions = attribute.attrOptions as Array<AttrOption>;
        // 获取当前选中项id列表
        let activeOptions = this.getActiveOptionsId(attributeOptions);
        // 获取目标项Option
        const targetOption = attributeOptions.find(item => {
            return item.id == targetId;
        })
        if (!targetOption) {
            throw new Error("");
        }
        // 目前项未被选中:选中目标项
        if (activeOptions.indexOf(targetId) == -1) {
            this.updateMultiDiscreteDirty(targetId, attribute);
            return attribute;
        }
        // 当前属性能否反选
        const canInvert = this.canInvert(attribute);
        // 目前项已被选中,且能反选处理
        if (activeOptions.indexOf(targetId) > -1 && canInvert) {
            this.updateMultiDiscreteDirty(targetId, attribute, true);
            return attribute;
        } else {
            // 目前项已被选中,且不能反选时,不作任何处理
            return attribute;
        }
    }


    /**
     * @description 检查是否存在已被用户修改的属性
     * @param attributeList 
     * @returns 
     */
    public static hasDirtyAttribute(attributeList: Array<SkuAttributeModel>): boolean {
        const hasDirty = attributeList.find(item => {
            return item.isDirty
        })
        return hasDirty ? true : false;
    }


    /********************************************************* */


    /**
     * @deprecated
     * @description 更新连续值类型的属性 
     */
    public static updateContinuousValue(value: string, attribute: SkuAttributeModel): qpmn.product.model.domain.propertyModel.ISkuAttributeModel | boolean {
        // 检查当前属性是否是连续型属性
        if (attribute.selectType != "NON") {
            throw new Error("");
        }
        // 属性值类型转换
        if (attribute.valueType == "Number") {
            attribute.value = Number(value);
        } else if (attribute.valueType == "String") {
            attribute.value = value;
        }

        // 将属性isDirty设为true
        attribute.isDirty = true;

        return attribute;
    }


    /**
     * 
     * @deprecated
     * @description 更新单离散类型属性值
     * @param selectedId 
     * @param attribute 
     * @returns 
     */
    public static updateSingleDiscreteValue(optionId: number, attribute: SkuAttributeModel): qpmn.product.model.domain.propertyModel.ISkuAttributeModel | boolean {
        // 检查属性是否是单离散类型
        if (attribute.selectType != "SINGLE") {
            throw new Error("Attribute is not single attribute");
        }
        if (!attribute.attrOptions) {
            throw new Error("Attribute don have attrOptions");
        }
        // 获取当前选中的所有属性选项
        let activeOptions = this.getActiveOption(attribute.attrOptions);

        // 找出目标options
        let targetOption = attribute.attrOptions.find(item => {
            return item.id == optionId;
        })

        if (!targetOption) {
            throw new Error("");
        }


        /********************************************************************** */

        // 已选中项为空
        if (!activeOptions) {
            targetOption.isSelected = true;
        } else if (activeOptions && activeOptions.length == 1 && activeOptions[0].id == optionId) {
            // 当前点击项和已选择项是同一个时,看能否反选            
            targetOption.isSelected = this.canInvert(attribute) ? false : true;
            if (!this.canInvert(attribute)) {
                return false;
            }
        } else if (activeOptions && activeOptions.length == 1 && activeOptions[0].id != optionId) {
            activeOptions[0].isSelected = false;
            targetOption.isSelected = true;
        }

        attribute.isDirty = true;
        attribute.value = attribute.value ? attribute.value as Array<number> : [];
        attribute.value.length = 0;
        attribute.value.push(optionId);

        return attribute;
    }



    /**
     * @description 通过attributeId获取指定的属性对象
     * @param attributeId 
     * @param profileModels 
     * @param profileKey 
     * @returns 
     */
    public static getAttributeById(attributeId: number, profileModels: Array<ProfileModel>, profileKey: string): SkuAttributeModel {
        if (angular.isUndefined(attributeId) || angular.isUndefined(profileModels) || angular.isUndefined(profileKey)) {
            throw new Error(`Necessary parameters are missing. The input parameter cannot be empty.attributeId:${attributeId},profileModels:${profileModels},profileKey:${profileKey}`);
        }

        let targetProfileIndex = profileModels.findIndex(profileAttr => {
            return profileAttr.profile.code == profileKey || profileAttr.profile._id == profileKey;
        })

        if (targetProfileIndex == -1) {
            throw new Error(`Can not find matched profile,profileKey为:${profileKey}`);
        }

        let matchedAttribute = profileModels[targetProfileIndex].attributeList.find(attributeItem => {
            return attributeItem.attributeId == attributeId;
        })
        if (!matchedAttribute) {
            throw new Error(`Can not find matched attribute,attributeId为:${attributeId}`);
        }
        return matchedAttribute;
    }


    /**
     * @desc 更新多离散值属性
     * @param optionId 
     * @param isSelect 
     * @param attribute 
     */
    public static updateMultiDiscreteValue(optionId: number, attribute: qpmn.product.model.domain.propertyModel.ISkuAttributeModel): qpmn.product.model.domain.propertyModel.ISkuAttributeModel | boolean {
        // 检查属性是否是单离散类型
        if (attribute.selectType != "MULTI") {
            throw new Error("Attribute is not MULTI attribute");
        }

        if (!attribute.attrOptions) {
            throw new Error("Attribute don have attrOptions");
        }
        // 获取当前选中项id列表
        let activeOptions = this.getActiveOptionsId(attribute.attrOptions);

        // 找出目标options
        let targetOption = attribute.attrOptions.find(item => {
            return item.id == optionId;
        })
        if (!targetOption) {
            throw new Error("");
        }


        // 目前项未被选中:选中目标项
        if (activeOptions.indexOf(optionId) == -1) {
            this.updateMultiDiscreteDirty(optionId, attribute);
            return attribute;
        } else {

            // 当前属性能否反选
            const canInvert = this.canInvert(attribute);

            // 目前项已被选中,且能反选处理
            if (activeOptions.indexOf(optionId) > -1 && canInvert) {
                this.updateMultiDiscreteDirty(optionId, attribute, true);
                return attribute;
            } else {
                // 目前项已被选中,且不能反选时,不作任何处理
                return attribute;
            }
        }
    }

    public static convertAllAttributeValue(propertyModel: PropertyModel): Array<AttributeValue> {
        let attributeList: Array<AttributeValue> = [];

        propertyModel.profileModels.forEach(profileItem => {
            const profileAttribute = this.convertToAttributeValue(profileItem.attributeList);
            attributeList = attributeList.concat(profileAttribute);
        })

        return attributeList;
    }

    public static convertToAttributeValue(attributeList: Array<SkuAttributeModel>): Array<AttributeValue> {
        let result: Array<AttributeValue> = [];

        attributeList.forEach((item, index, array) => {
            let _attributeValue = new AttributeValue();
            _attributeValue.attributeId = item.attributeId;
            _attributeValue.attribute = new Attribute();
            _attributeValue.attribute.id = item.attributeId;
            _attributeValue.attribute.required = item.required;
            _attributeValue.skuAttributeId = item.skuAttributeId;
            _attributeValue.skuAttributeCode = item.skuAttributeCode;
            _attributeValue.isSku = item.isSku;
            if (item.attrOptions) {
                let _opt: Array<AttributeOption> = item.attrOptions;
                // TODO 临时处理待优化
                _opt.forEach(item => {
                    item.clazz = "com.qpp.cgp.domain.attribute.AttributeOption";
                });
                _attributeValue.attribute.options = _opt as Array<AttributeOption>;
                let _attrVal = item.value as Array<number>;
                if (!_attrVal) {
                    _attrVal = [];
                }
                // 获取属性值optionValue数组
                const _attrValValue:Array<any> = [];
                for(let i=0;i<_opt.length;i++){
                    const option = _opt[i];
                    if(_attrVal.indexOf(option.id) !==-1){
                        _attrValValue.push(option.value);
                    }
                }
                _attributeValue.optionValues = _attrValValue.join(',');
                _attributeValue.optionIds = _attrVal.join(",");
            } else {
                _attributeValue.value = item.value as string;
            }
            result.push(_attributeValue);
        })
        return result;
    }

    /**
     * @deprecated 2022-11-30
     * @param attributeList 
     * @returns 
     */
    public static convertToAttribute(attributeList: Array<SkuAttributeModel>): Array<Attribute> {
        let result: Array<Attribute> = [];

        attributeList.forEach((item, index, array) => {
            let _attribute = new Attribute();
            _attribute.id = item.attributeId;
            _attribute.required = item.required;
            let _opt = item.attrOptions as unknown;
            _attribute.options = _opt as Array<AttributeOption>;
            // TODO(Shirley 11-19)
            // let tempObj = {
            //     attribute:_attribute,
            //     // attributeId : item.attributeId,
            //     // displayName:item.displayName,
            //     // valueType:item.valueType,
            //     // value: item.value,
            //     // groupId:item.groupId,
            // }

            result.push(_attribute);
        })
        return result;
    }

    /**
     * @description 通过AttributeValue更新SkuAttributeModel属性值
     */
    public static updateSkuAttributeModelByAttributeValue(attributeValues: Array<AttributeValue>, attributeList: Array<SkuAttributeModel>): Array<SkuAttributeModel> {
        // 遍历attributeValues,在AtttributeProfile中找到对应attributeId的SkuAttributeModel对象,并将当前属性值赋值给SkuAttributeModel
        attributeValues.forEach(attItem => {
            // 找出指定attributeId的SkuAttributeModel
            let matchAttr = attributeList.find((item) => {
                return item.attributeId == attItem.attributeId;
            })
            if (matchAttr) {
                // 单离散值属性赋值
                if (matchAttr.selectType == "SINGLE" && matchAttr.attrOptions && matchAttr.attrOptions.length > 0) {
                    // 当前选中的optionId
                    let selectedOption = Number(attItem.optionIds);
                    // 将当前选中的id赋值给匹配的SkuAttributeModel的value值中
                    let attrVal = matchAttr.value as Array<number> || [];
                    attrVal.length = 0;
                    attrVal.push(Number(selectedOption));
                    // 设置option的选中状态
                    matchAttr.attrOptions.forEach(optItem => {
                        if (optItem.id == selectedOption) {
                            optItem.isSelected = true
                        } else {
                            optItem.isSelected = false;
                        }
                    })
                }
                // 多离散值赋值
                if (matchAttr.selectType == "MULTI" && matchAttr.attrOptions && matchAttr.attrOptions.length > 0) {
                    // 当前选中的optionId
                    let selectedOption = attItem.optionIds?.split(",");
                    // 将当前选中的id赋值给匹配的SkuAttributeModel的value值中
                    let attrVal = matchAttr.value as Array<number>;
                    attrVal.length = 0;
                    // 设置value
                    selectedOption?.forEach(i => {
                        attrVal.push(Number(i));
                    })
                    // matchAttr.value = attItem.optionIds?.split(",");
                    matchAttr.attrOptions.forEach(optItem => {
                        if (attItem.optionIds && attItem.optionIds?.indexOf(String(optItem.id)) > -1) {
                            optItem.isSelected = true;
                        } else {
                            optItem.isSelected = false;
                        }
                    })
                }
                // 连续型属性赋值
                if (matchAttr.selectType == "NON") {
                    // TODO 临时处理-根据属性类型转为属性值类型
                    let value: string | number | boolean | number[] = attItem.value;
                    if (typeof value === 'string') {
                        if (matchAttr.valueType == 'Boolean') {
                            value = (value === 'true') ? true : false;
                        }
                        if (matchAttr.valueType == 'Number') {
                            value = Number(value);
                        }
                    }
                    matchAttr.value = value;
                }
            }
        })
        return attributeList;
    }

    /**
     * 
     */
    public static getProfileByKey(profileKey: string, profileModels: Array<ProfileModel>): Profile {
        const matchedProfile = profileModels.find(item => {
            return item.profile.code == profileKey || item.profile._id == profileKey;
        })
        if (!matchedProfile) {
            throw new Error("");
        }
        return matchedProfile.profile;
    }


    public getProfileModel(profileKey: string, profileModels: Array<ProfileModel>): ProfileModel | undefined {
        const matchedProfileModel = profileModels.find(pItem => {
            return pItem.profile.code == profileKey || pItem.profile._id == profileKey;
        })
        return matchedProfileModel;
    }
}